// INP OPTIMIZED VERSION-5
import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Ultra optimized Variant Picker
 *
 * Main goals:
 * - Reduce INP
 * - Reduce main-thread blocking
 * - Reduce large DOM morphs
 * - Prevent unnecessary reflows
 * - Abort stale requests
 * - Schedule heavy work outside interaction frame
 */

export default class VariantPicker extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {Map<string, string>} — cache of requestUrl → responseText */
  #responseCache = new Map();

  /** @type {string | null} — last resolved price for the ADD button */
  #atcPrice = null;

  /** @type {string | null} — last resolved compare-at price for the ADD button */
  #atcCompare = null;

  /** @type {MutationObserver | null} — re-asserts the ADD button price if wiped */
  #atcPriceObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.onVariantChange = this.variantChanged.bind(this);

    this.addEventListener('change', this.onVariantChange, {
      passive: true,
    });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onVariantChange);
    this.#atcPriceObserver?.disconnect();
    this.#atcPriceObserver = null;
  }

  /**
   * Variant change handler
   * @param {Event} event
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const target = event.target;

    this.updateSelectedOption(target);

    this.dispatchEvent(
      new VariantSelectedEvent({
        id: target.dataset.optionValueId ?? '',
      })
    );

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !target.closest('product-card') &&
      !target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = target.dataset.connectedProductUrl;

    const loadsNewProduct =
      isOnProductPage &&
      !!newUrl &&
      newUrl !== currentUrl;

    const requestUrl = this.buildRequestUrl(target);

    // Update URL immediately (cheap task)
    this.updateBrowserUrl(target, loadsNewProduct, newUrl);

    // ── Instant price update from data attributes (no fetch needed) ──
    // data-product-price is already embedded on each variant button by Liquid.
    // Update the visible price immediately so user sees it change on click,
    // before the Section Rendering fetch completes.
    this.updatePriceInstant(target);

    // Defer fetch slightly to unblock interaction paint
    requestAnimationFrame(() => {
      this.fetchUpdatedSection({
        requestUrl,
        shouldMorphMain: loadsNewProduct,
      });
    });
  }

  /**
   * Optimized fetch/update pipeline
   */
  async fetchUpdatedSection({
    requestUrl,
    shouldMorphMain = false,
  }) {
    try {
      // Abort previous in-flight request
      this.#abortController?.abort();
      this.#abortController = new AbortController();

      let responseText;

      // Cache hit — instant, no network request
      if (this.#responseCache.has(requestUrl)) {
        responseText = this.#responseCache.get(requestUrl);
      } else {
        const response = await fetch(requestUrl, {
          signal: this.#abortController.signal,
        });
        responseText = await response.text();
        // Store in cache (max 20 entries — covers all variants of a product)
        if (this.#responseCache.size >= 20) {
          this.#responseCache.delete(this.#responseCache.keys().next().value);
        }
        this.#responseCache.set(requestUrl, responseText);
      }

      this.#pendingRequestUrl = undefined;

      // Yield back to browser before parsing
      await this.nextFrame();

      const html = new DOMParser().parseFromString(
        responseText,
        'text/html'
      );

      html
        .querySelector('overflow-list[defer]')
        ?.removeAttribute('defer');

      const textContent = html.querySelector(
        'variant-picker script[type="application/json"]'
      )?.textContent;

      if (!textContent) return;

      // Yield again before DOM mutations
      await this.nextFrame();

      if (shouldMorphMain) {
        this.updateCriticalSections(html);
      } else {
        this.updateVariantPicker(html);
      }

      // Dispatch event after paint-sensitive work
      if (this.selectedOptionId) {
        this.dispatchEvent(
          new VariantUpdateEvent(
            JSON.parse(textContent),
            this.selectedOptionId,
            {
              html,
              productId: this.dataset.productId ?? '',
            }
          )
        );
      }

      // Update buttons separately
      this.updateAddToCartButtons(html);

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
      }
    }
  }

  /**
   * Only morph critical sections
   * Avoid full-page morphing
   */
  updateCriticalSections(newHtml) {
    const selectors = [
      '.product-gallery',
      '.product-info',
      '.product-price',
      '.product-form',
      '.product-inventory',
    ];

    selectors.forEach((selector) => {
      const currentEl = document.querySelector(selector);
      const newEl = newHtml.querySelector(selector);

      if (currentEl && newEl) {
        morph(currentEl, newEl);
      }
    });
  }

  /**
   * Smaller morph scope for better INP
   */
  updateVariantPicker(newHtml) {
    const newVariantPicker = newHtml.querySelector(
      this.tagName.toLowerCase()
    );

    if (!newVariantPicker) return;

    // Update dataset only
    if (newVariantPicker instanceof HTMLElement) {
      this.dataset.productId =
        newVariantPicker.dataset.productId;

      this.dataset.productUrl =
        newVariantPicker.dataset.productUrl;
    }

    // Morph only options area
    const currentOptions = this.querySelector(
      '.variant-picker__options'
    );

    const newOptions = newVariantPicker.querySelector(
      '.variant-picker__options'
    );

    if (currentOptions && newOptions) {
      morph(currentOptions, newOptions);
    }
  }

  /**
   * Lightweight ATC update
   */
  updateAddToCartButtons(html) {
    requestAnimationFrame(() => {
      const ajaxButton = html.querySelector(
        '.product-details add-to-cart-component button'
      );

      const currentButton = document.querySelector(
        '.product-details add-to-cart-component button'
      );

      if (ajaxButton && currentButton) {
        currentButton.replaceWith(ajaxButton);
      }

      // Buy more buttons
      const ajaxButtons = html.querySelectorAll(
        '.product__buymore-list add-to-cart-component button'
      );

      const currentButtons = document.querySelectorAll(
        '.product__buymore-list add-to-cart-component button'
      );

      currentButtons.forEach((btn, i) => {
        if (ajaxButtons[i]) {
          btn.replaceWith(ajaxButtons[i]);
        }
      });

      // Keep the custom price inside the ADD button in sync with the variant.
      this.syncAddToCartPrice(html);
    });
  }

  /**
   * Sync the custom `.add-to-cart-price` shown inside the ADD button with the
   * resolved variant.
   *
   * Multi-option products (e.g. Size + Pack) don't expose a usable
   * `data-product-price` on a single option input, so the synchronous
   * `updatePriceInstant` can't update it. Here we read the resolved variant's
   * server-formatted price from the freshly fetched product-card HTML, remember
   * it, write it into the button, and keep watching: the Loop subscription
   * widget re-renders the ADD button ~500ms after a variant change and would
   * otherwise wipe the price, so we re-assert it whenever it gets cleared.
   *
   * @param {Document} html — parsed fetched section HTML
   */
  syncAddToCartPrice(html) {
    const newPrice = html.querySelector('product-price .price')?.textContent?.trim();
    if (!newPrice) return;

    this.#atcPrice = newPrice;
    this.#atcCompare =
      html.querySelector('product-price .compare-at-price')?.textContent?.trim() || null;

    this.applyAtcPrice();
    this.watchAtcPrice();
  }

  /**
   * Resolve the nearest element (quick-add dialog or product section) that
   * holds the ADD button, used both to read and to observe it.
   * @returns {Element}
   */
  atcScope() {
    const scope =
      this.closest('dialog, .shopify-section') || document.getElementById('quick-add-dialog');
    return scope instanceof Element ? scope : document.body;
  }

  /**
   * Write the remembered price/compare into the `.add-to-cart-price` span.
   * Idempotent and loop-safe: only mutates when the value actually differs.
   */
  applyAtcPrice() {
    const price = this.#atcPrice;
    if (!price) return;

    const atcPriceEl = this.atcScope().querySelector('.add-to-cart-price');
    if (!atcPriceEl) return;

    // Replace only the leading price text node — never destroy child elements
    // (the compare-at price lives in a child <span>).
    const priceText = ' ' + price + ' ';
    const [firstTextNode, ...restTextNodes] = Array.from(atcPriceEl.childNodes).filter(
      (node) => node.nodeType === Node.TEXT_NODE
    );

    if (firstTextNode) {
      if (firstTextNode.textContent !== priceText) firstTextNode.textContent = priceText;
      restTextNodes.forEach((node) => {
        if (node.textContent) node.textContent = '';
      });
    } else {
      atcPriceEl.insertBefore(document.createTextNode(priceText), atcPriceEl.firstChild);
    }

    // Compare-at (strikethrough) price
    let compareEl = atcPriceEl.querySelector('.compare_at_price, .compare-at-price');

    if (this.#atcCompare) {
      if (!compareEl) {
        compareEl = document.createElement('span');
        compareEl.className = 'compare_at_price';
        compareEl.appendChild(document.createElement('s'));
        atcPriceEl.appendChild(compareEl);
      }
      if (compareEl instanceof HTMLElement) compareEl.style.display = '';
      const sTag = compareEl.querySelector('s') || compareEl;
      if (sTag.textContent !== this.#atcCompare) sTag.textContent = this.#atcCompare;
    } else if (compareEl instanceof HTMLElement) {
      compareEl.style.display = 'none';
    }
  }

  /**
   * Watch the ADD button's scope and re-assert the price whenever an external
   * re-render (e.g. the Loop widget's delayed update) wipes it. Loop-safe: only
   * re-applies when the price text is actually missing, so our own writes don't
   * retrigger it.
   */
  watchAtcPrice() {
    const scope = this.atcScope();

    this.#atcPriceObserver?.disconnect();
    this.#atcPriceObserver = new MutationObserver(() => {
      const atcPriceEl = scope.querySelector('.add-to-cart-price');
      if (!atcPriceEl) return;

      const hasPrice = Array.from(atcPriceEl.childNodes).some(
        (node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim()
      );

      if (!hasPrice) this.applyAtcPrice();
    });

    this.#atcPriceObserver.observe(scope, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  /**
   * Instantly update the price display from data attributes on the variant button.
   * Called synchronously on click — no fetch, no wait.
   * Works on both desktop and mobile (where product-price is moved to product-header).
   * @param {HTMLElement} target — the clicked variant input
   */
  updatePriceInstant(target) {
    const newPrice   = target.dataset.productPrice;
    const newCompare = target.dataset.comparePrice;

    if (!newPrice) return;

    // Use closest dialog/section ancestor first — most reliable context.
    // Fall back to #quick-add-dialog for cases where variant-picker is outside dialog DOM.
    const dialog = target.closest('dialog, .shopify-section')
                || document.getElementById('quick-add-dialog')
                || document;

    /* ── 1. Update product-price component ── */
    // Try scoped dialog first, fall back to broader selector (handles dynamic modal content)
    const priceEl = dialog.querySelector('product-price [ref="priceContainer"] .price')
                 || dialog.querySelector('[ref="priceContainer"] .price');
    if (priceEl) priceEl.textContent = newPrice;

    const compareEl = dialog.querySelector('product-price [ref="priceContainer"] .compare-at-price')
                   || dialog.querySelector('[ref="priceContainer"] .compare-at-price');
    if (compareEl) {
      const hasCompare = newCompare && newCompare !== '$0.00' && newCompare !== '£0.00' && newCompare !== '€0,00';
      compareEl.style.display = hasCompare ? '' : 'none';
      if (hasCompare) compareEl.textContent = newCompare;
    }

    /* ── 2. Update .add-to-cart-price span (primary price on mobile) ──
       Structure: text node ("$7.99") + optional <span class="compare_at_price"><s>...</s></span>
       Only replace the text node — never destroy child elements. */
    const atcPriceEl = dialog.querySelector('.add-to-cart-price');
    if (atcPriceEl) {
      const textNodes = Array.from(atcPriceEl.childNodes)
        .filter(n => n.nodeType === Node.TEXT_NODE);

      if (textNodes.length > 0) {
        textNodes[0].textContent = ' ' + newPrice + ' ';
        textNodes.slice(1).forEach(n => { n.textContent = ''; });
      } else {
        const textNode = document.createTextNode(' ' + newPrice + ' ');
        atcPriceEl.insertBefore(textNode, atcPriceEl.firstChild);
      }

      const atcCompareEl = atcPriceEl.querySelector('.compare_at_price, .compare-at-price');
      if (atcCompareEl) {
        const hasCompare = newCompare && newCompare !== '$0.00' && newCompare !== '£0.00' && newCompare !== '€0,00';
        atcCompareEl.style.display = hasCompare ? '' : 'none';
        if (hasCompare) {
          const sTag = atcCompareEl.querySelector('s') || atcCompareEl;
          sTag.textContent = newCompare;
        }
      }
    }

    /* ── 3. Re-enable ATC button immediately (not after fetch) ── */
    requestAnimationFrame(() => {
      const atcBtn = dialog.querySelector('add-to-cart-component button.add-to-cart-button');
      if (atcBtn && atcBtn.disabled) atcBtn.disabled = false;
    });
  }

  /**
   * Browser URL update
   */
  updateBrowserUrl(target, loadsNewProduct, newUrl) {
    const url = new URL(window.location.href);

    if (Theme.template.name === 'product') {
      if (target.dataset.variantId) {
        url.searchParams.set(
          'variant',
          target.dataset.variantId
        );
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct && newUrl) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Yield to next frame
   */
  nextFrame() {
    return new Promise((resolve) => {
      requestAnimationFrame(resolve);
    });
  }

  /**
   * Update selected option
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      target = this.querySelector(
        `[data-option-value-id="${target}"]`
      );

      if (!target) return;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const selectedOption = Array.from(
        target.options
      ).find((option) => option.value === target.value);

      if (!selectedOption) return;

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      selectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Build request URL
   */
  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = []
  ) {
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;

    this.#pendingRequestUrl = productUrl;

    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(
        `option_values=${this.selectedOptionsValues.join(',')}`
      );
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(
          `option_values=${sourceSelectedOptionsValues.join(',')}`
        );
      } else {
        params.push(
          `option_values=${selectedOption.dataset.optionValueId}`
        );
      }
    }

    if (
      this.closest('quick-add-component') ||
      this.closest('swatches-variant-picker-component')
    ) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }

      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Selected option
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[selected], fieldset input:checked'
    );

    if (
      !(
        selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement
      )
    ) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Selected option ID
   */
  get selectedOptionId() {
    const { selectedOption } = this;

    if (!selectedOption) return undefined;

    return selectedOption.dataset.optionValueId;
  }

  /**
   * Selected option values
   */
  get selectedOptionsValues() {
    return Array.from(
      this.querySelectorAll(
        'select option[selected], fieldset input:checked'
      )
    ).map((option) => option.dataset.optionValueId);
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define(
    'variant-picker',
    VariantPicker
  );
}